<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            <strong>Google Street View</strong> ist für viele Kunden der erste
            Blick in Ihr Unternehmen — noch bevor sie vorbeikommen. Wenn Ihre
            Innenräume als verknüpfte 360°-Panoramen auf Google Maps und in
            Ihrem Google-Business-Profil erscheinen, können Nutzer in der Suche,
            in Maps oder in der <strong>Street View App</strong> durch Ihre
            Räume gehen.
          </p>
          <p>
            Mit Travvir veröffentlichen Sie aus der App — ohne privates
            Google-Konto. Die Bilder liegen an <em>Ihrem</em> Standort auf
            Google Maps; <strong>Travvir ist der Street-View-Publisher</strong>
            an diesem Ort.
          </p>
          <p>
            Zwei Wege: Bei einem <strong>einzelnen Scan</strong> wählen Sie den
            Ort auf Google Maps, wählen die Szenen und veröffentlichen sie auf
            Street View. Bei einer <strong>360°-Tour</strong> wählen Sie den Ort
            auf Google Maps und veröffentlichen die verknüpfte Tour direkt.
          </p>
        </section>

        <section>
          <h2>Was ist Google Street View für Unternehmen?</h2>
          <p>
            Gemeint sind <strong>verknüpfte Innen-Panoramen</strong> an einer
            echten Adresse — sichtbar in Maps, in der Google-Suche (Business-
            Profil) und in der Street View App als „Innen ansehen“ oder blaue
            Punkte auf der Karte.
          </p>
        </section>

        <section>
          <h2>So funktioniert die Veröffentlichung heute</h2>
          <p>
            Zuerst wählen Sie den passenden <strong>Google-Maps-Ort</strong>
            (Name und Pin). Travvir sendet die Panoramen über sein autorisiertes
            Street-View-Publisher-Konto. Auf Street View erscheint
            <strong>Travvir als Publisher</strong> an diesem Ort; die Abdeckung
            bleibt an Ihrer Adresse. Google prüft die Bilder vor der Freigabe.
          </p>
        </section>

        <section>
          <h2>So veröffentlichen Sie mit Travvir auf Google Street View</h2>
          <p>
            Aufnehmen am Smartphone, dann Teilen → Google Street View. Derselbe
            Ablauf steht auf der
            <a :href="`${mainSiteUrl}/google-streetview`"
              >Google-Street-View-Seite</a
            >.
          </p>
          <h3>Einzelner Scan</h3>
          <p>
            Ort auf Google Maps wählen, Szenen auswählen, dann auf Street View
            veröffentlichen. Nur vollständige 360°-Panoramen; der Pin muss zum
            richtigen Gebäude gehören.
          </p>
          <h3>360°-Tour</h3>
          <p>
            Ort auf Google Maps wählen und die verknüpfte Tour direkt
            veröffentlichen — ohne jede Szene einzeln hochzuladen.
          </p>
          <p>
            Verarbeitung oft in Minuten; Google-Prüfung meist
            <strong>24–72 Stunden</strong>.
          </p>
          <p>
            Auf Google sehen Nutzer Panoramen und Pfeil-Navigation zwischen
            Szenen — keine Travvir-Hotspots oder Branding-Elemente. Diese bleiben
            auf Ihrer eingebetteten Travvir-Tour.
          </p>
        </section>

        <section>
          <h2>Anforderungen</h2>
          <ul>
            <li>Nur equirektangulare 360°-JPEGs (keine 2D-Fotos).</li>
            <li>
              Auflösung nach Google-Mindestanforderungen (siehe
              <a href="/blog/de/360-virtual-tour-bildgroesse"
                >360°-Bildgrößen</a
              >).
            </li>
            <li>
              Korrekter Maps-Ort, damit Google die Panoramen der richtigen Fiche
              zuordnet. Travvir ist der Street-View-Publisher an diesem Ort.
            </li>
          </ul>
        </section>

        <section>
          <h2>Street View App oder Travvir?</h2>
          <p>
            Die kostenlose <strong>Street View App</strong> eignet sich für eine
            schnelle Einzel-Sphere. Travvir lohnt sich für mehrere Räume, bessere
            Tour-Logik und Direktveröffentlichung des gesamten Rundgangs.
          </p>
        </section>

        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li>
              <a :href="`${mainSiteUrl}/google-streetview`"
                >Google Street View mit Travvir</a
              >
            </li>
            <li>
              <a href="/blog/de/google-street-view-virtual-tour"
                >Google Street View Virtual Tour Übersicht</a
              >
            </li>
            <li>
              <a href="/blog/de/google-maps-360">Google Maps 360 Leitfaden</a>
            </li>
          </ul>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";
const postData = blogPostsData.find((p) => p.slug === "google-street-view");
const config = useRuntimeConfig();
const mainSiteUrl = config.public.baseUrl;

useBlogSeo({
  title:
    "Google Street View: 360°-Tour mit Travvir veröffentlichen [2026] | Travvir",
  description:
    "Einzelnen Scan oder 360°-Tour auf Google Street View veröffentlichen: Ort auf Maps wählen; Travvir ist der Publisher an diesem Standort.",
  keywords:
    "google street view, google maps 360, street view app, 360 tour google maps veröffentlichen",
  slug: "google-street-view",
  image: postData?.image,
  postData,
  lang: "de",
});
</script>
